// App.js - Wilout UI Brandbook Layout
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter as Router } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
// Import du design system officiel Wilout UI
import '@wilout/ui-css/dist/ui.css';
import './styles/App.css';
import './styles/ModalStyles.css';
import './styles/responsive.css';
// Chargés après responsive.css : le socle doit pouvoir corriger les overrides
// historiques du gros bloc 768px, pas l'inverse.
import './styles/responsive-foundation.css';
import './styles/responsive-table.css';
import './styles/ProgressGradient.css';

import Sidebar from './components/Sidebar';
import ErrorBoundary from './components/ErrorBoundary';
import AppRoutes from './routes/AppRoutes';
import TopProgressBar from './components/TopProgressBar';
import GuideAcknowledgment, { GUIDE_VERSION } from './components/GuideAcknowledgment';
import RequiredVideosGate from './components/RequiredVideosGate';
import { modalCleanup } from './utils/modalCleanup';
import { useWilout } from './context/WiloutContext';
import { useRoles } from './context/RolesContext';
import api, { initApiAuth, getRequiredVideos } from "./api/api";

// Lazy load heavy components not needed at initial render

// Gestionnaire d'erreur global pour les CanceledError
const setupGlobalErrorHandling = () => {
  const originalErrorHandler = window.onerror;
  const originalUnhandledRejectionHandler = window.onunhandledrejection;

  window.onerror = (message, source, lineno, colno, error) => {
    if (error && (error.name === 'CanceledError' || error.message?.includes('canceled'))) {
      return true;
    }
    if (originalErrorHandler) {
      return originalErrorHandler(message, source, lineno, colno, error);
    }
    return false;
  };

  window.onunhandledrejection = (event) => {
    if (event.reason && (event.reason.name === 'CanceledError' || event.reason.message?.includes('canceled'))) {
      event.preventDefault();
      return;
    }
    if (originalUnhandledRejectionHandler) {
      originalUnhandledRejectionHandler(event);
    }
  };

  return () => {
    window.onerror = originalErrorHandler;
    window.onunhandledrejection = originalUnhandledRejectionHandler;
  };
};

function AppWrapper() {
  const { t } = useTranslation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarMobileOpen, setSidebarMobileOpen] = useState(false);
  const [showGuideModal, setShowGuideModal] = useState(false);
  const [guideChecked, setGuideChecked] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);
  // Vidéos à voir avant d'accéder à opé : null = vérification en cours, [] = rien à voir (ou admin).
  const [requiredVideos, setRequiredVideos] = useState(null);

  const {
    isAuthenticated,
    isLoading: auth0Loading,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
  } = useAuth0();

  const {
    user,
    setUser,
    events
  } = useWilout();

  const { ready: rolesReady, error: rolesError, roles, loadRoles } = useRoles();

  // Configuration de la gestion d'erreur globale
  useEffect(() => {
    const cleanup = setupGlobalErrorHandling();
    return cleanup;
  }, []);

  // Initialize axios interceptor with Auth0 token getter
  useEffect(() => {
    if (isAuthenticated) {
      initApiAuth(getAccessTokenSilently, auth0Logout);
    }
  }, [isAuthenticated, getAccessTokenSilently, auth0Logout]);

  // Load user profile from MongoDB once authenticated
  useEffect(() => {
    if (!isAuthenticated || profileLoaded) return;

    const loadProfile = async () => {
      try {
        const res = await api.get('/auth/me');
        setUser(res.data);
      } catch (err) {
        console.error('Erreur chargement profil:', err);
      } finally {
        setProfileLoaded(true);
      }
    };

    loadProfile();
  }, [isAuthenticated, profileLoaded, setUser]);

  // Relire le profil au retour sur l'onglet (au plus une fois par minute) : un
  // changement de rôle ou d'équipe fait par un admin s'applique sans F5. L'API,
  // elle, relit toujours l'utilisateur en base à chaque requête.
  useEffect(() => {
    if (!isAuthenticated || !profileLoaded) return undefined;
    let lastRefresh = Date.now();
    const onFocus = async () => {
      if (Date.now() - lastRefresh < 60 * 1000) return;
      lastRefresh = Date.now();
      try {
        const res = await api.get('/auth/me');
        setUser((prev) => (prev && prev.role === res.data.role && prev.team === res.data.team && prev.teamAutonomous === res.data.teamAutonomous ? prev : res.data));
      } catch (err) {
        console.error('Erreur rafraîchissement profil:', err);
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [isAuthenticated, profileLoaded, setUser]);

  // Redirect to Auth0 login if not authenticated
  useEffect(() => {
    if (!auth0Loading && !isAuthenticated) {
      loginWithRedirect();
    }
  }, [auth0Loading, isAuthenticated, loginWithRedirect]);

  // Vidéos obligatoires (tous les rôles sauf admin). En cas d'erreur, l'accès reste ouvert : une panne de ce
  // contrôle ne doit pas bloquer toute l'équipe.
  useEffect(() => {
    if (!user || requiredVideos !== null) return;
    getRequiredVideos()
      .then((data) => setRequiredVideos(data?.required ? data.videos : []))
      .catch((err) => {
        console.error('Erreur vérification vidéos obligatoires:', err);
        setRequiredVideos([]);
      });
  }, [user, requiredVideos]);

  // Vérifier si l'utilisateur a signé la décharge du guide
  useEffect(() => {
    if (!user || guideChecked) return;
    const checkGuide = async () => {
      try {
        const res = await api.get('/users/guide-status');
        const ack = res.data?.guideAcknowledged;
        if (!ack || (ack.version || 0) < GUIDE_VERSION) {
          setShowGuideModal(true);
        }
      } catch (err) {
        console.error('Erreur vérification guide:', err);
        setShowGuideModal(true);
      } finally {
        setGuideChecked(true);
      }
    };
    checkGuide();
  }, [user, guideChecked]);

  const handleGuideAcknowledge = useCallback(async (version) => {
    try {
      await api.post('/users/guide-acknowledged', { version });
      setShowGuideModal(false);
      modalCleanup();
    } catch (err) {
      console.error('Erreur signature guide:', err);
    }
  }, []);

  const handleToggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleToggleMobileSidebar = () => {
    setSidebarMobileOpen(prev => !prev);
  };

  const handleCloseMobileSidebar = () => {
    setSidebarMobileOpen(false);
  };

  // Lock body scroll when mobile sidebar is open to prevent layout shift
  useEffect(() => {
    if (sidebarMobileOpen) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [sidebarMobileOpen]);

  // Rôles introuvables après retry : sans eux toutes les permissions valent false,
  // mieux vaut le dire que d'afficher une app vide et des "Accès refusé".
  if (user && rolesReady && rolesError && roles.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100" style={{ backgroundColor: 'var(--ui-color-bg-page)' }}>
        <div className="text-center">
          <p style={{ color: 'var(--ui-color-text-muted)' }}>{t('common.rolesLoadError')}</p>
          <button type="button" className="btn btn-primary" onClick={() => loadRoles()}>{t('common.retryLoading')}</button>
        </div>
      </div>
    );
  }

  // Loading state: Auth0 loading, profile not yet fetched, or roles not yet loaded
  // (sans rôles, toutes les permissions valent false : flash "Accès refusé").
  if (auth0Loading || (isAuthenticated && !profileLoaded) || !user || !rolesReady || requiredVideos === null) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100" style={{ backgroundColor: 'var(--ui-color-bg-page)' }}>
        <div className="text-center">
          <div className="spinner-border" style={{ color: 'var(--ui-color-primary)', width: '3rem', height: '3rem' }} role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
          <p className="mt-3" style={{ color: 'var(--ui-color-text-muted)' }}>{t('common.loadingApp')}</p>
        </div>
      </div>
    );
  }

  if (requiredVideos.length > 0) {
    return (
      <RequiredVideosGate
        initialVideos={requiredVideos}
        onDone={() => setRequiredVideos([])}
        onLogout={() => auth0Logout({ logoutParams: { returnTo: window.location.origin } })}
      />
    );
  }

  return (
    <Router>
      <TopProgressBar />
      <GuideAcknowledgment show={showGuideModal} onAcknowledge={handleGuideAcknowledge} />
      <ToastContainer position="top-right" autoClose={3500} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover theme="colored" />
      <div className="app-layout">
        <Sidebar
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={handleToggleSidebar}
          isMobileOpen={sidebarMobileOpen}
          onCloseMobile={handleCloseMobileSidebar}
        />
        {/* Mobile overlay */}
        {sidebarMobileOpen && (
          <div className="sidebar-mobile-overlay" onClick={handleCloseMobileSidebar} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCloseMobileSidebar(e); } }} />
        )}
        <main className={`app-main ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
          {/* Mobile hamburger button */}
          <button
            className="sidebar-mobile-toggle"
            onClick={handleToggleMobileSidebar}
            aria-label={t('sidebar.openMenu')}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          </button>
          <div className="app-content">
            <ErrorBoundary>
              <AppRoutes user={user} events={events} />
            </ErrorBoundary>
          </div>
        </main>
      </div>
    </Router>
  );
}

export default AppWrapper;
